import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { navigateToCaseReport, getCaseReportUrl } from '@/services/navigation';

import moment from 'moment';
import { App } from '@/services/app-compat';
import { getModuleType } from '@/utils/legacy-utils';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const displayNocValuesForGridTemplate = _wg('displayNocValuesForGridTemplate');

interface NPSDReportsGridProps {
  reports: any[];
  title?: string;
  entityType?: string;
  sortable?: boolean;
  showRoles?: boolean;
  showOffenses?: boolean;
  personCodes?: any[];
  offenseCodes?: any[];
  onViewReport?: (reportId: any) => void;
}

export function NPSDReportsGrid({
  reports = [],
  title = 'Reports',
  entityType = 'entity',
  sortable = false,
  showRoles = false,
  showOffenses = false,
  personCodes = [],
  offenseCodes = [],
  onViewReport,
}: NPSDReportsGridProps) {
  const { theme } = useTheme();

  const [isMobile, setIsMobile] = useState(false);
  const [sortField, setSortField] = useState('callDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Check if mobile (debounced to avoid excessive re-renders during resize)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const displayReports = useMemo((): any[] => {
    if (!reports || reports.length === 0) return [];

    if (!sortable) {
      return reports;
    }

    const sorted = [...reports];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'callDate') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
      }
      if (typeof bVal === 'string') {
        bVal = (bVal || '').toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return sorted;
  }, [reports, sortable, sortField, sortDirection]);

  const recordCount = useMemo((): number => {
    return reports?.length || 0;
  }, [reports]);

  const toggleSort = useCallback((field: string) => {
    if (!sortable) return;
    setSortField(prev => {
      if (prev === field) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return prev;
      } else {
        setSortDirection('asc');
        return field;
      }
    });
  }, [sortable]);

  const getSortIcon = useCallback((field: string): string => {
    if (!sortable) return '';
    if (sortField !== field) return 'fa-sort';
    return sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }, [sortable, sortField, sortDirection]);

  const formatDateTime = useCallback((dateStr: string): string => {
    if (!dateStr) return '';
    if (typeof moment !== 'undefined') {
      return moment(dateStr).format('MM/DD/YYYY h:mm A');
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString();
  }, []);

  const formatNatureOfCall = useCallback((report: any): string => {
    if (typeof (window as any).displayNocValuesForGridTemplate === 'function') {
      return displayNocValuesForGridTemplate(report);
    }
    if (report.natureOfCallValues && Array.isArray(report.natureOfCallValues)) {
      return report.natureOfCallValues.join(', ');
    }
    return '';
  }, []);

  const getRolesDisplay = useCallback((roles: number[]): string => {
    if (!roles || roles.length === 0) return '';
    const roleNames: string[] = [];
    for (const roleCode of roles) {
      const found = personCodes.find((d: any) => d.code?.toString() === roleCode?.toString());
      if (found) {
        roleNames.push(found.value);
      }
    }
    return roleNames.join(' / ');
  }, [personCodes]);

  const getOffensesDisplay = useCallback((offenses: number[]): string => {
    if (!offenses || offenses.length === 0) return '';
    const offenseNames: string[] = [];
    for (const offenseCode of offenses) {
      const found = offenseCodes.find((d: any) => d.code === offenseCode);
      if (found) {
        offenseNames.push(found.value);
      }
    }
    return offenseNames.join(' / ');
  }, [offenseCodes]);

  const getReportLink = useCallback((report: any): string => {
    const reportId = report.id || report.reportId;
    return getCaseReportUrl(reportId);
  }, []);

  const viewReport = useCallback((report: any) => {
    const reportId = report.id || report.reportId;
    navigateToCaseReport(reportId, true);
    onViewReport?.(reportId);
  }, [onViewReport]);

  return (
    <div className={`npsd-reports-grid ${theme}`}>
      <div className="panel">
        <div className="panel-header">
          <span>{title}</span>
          <span className="record-count">({recordCount} records)</span>
        </div>
        <div className="panel-body">
          {/* Desktop Table */}
          {!isMobile ? (
            <div className="table-container">
              {displayReports.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th
                        style={{ width: '75px' }}
                        className={sortable ? 'sortable' : ''}
                        onClick={() => toggleSort('caseId')}
                      >
                        Case ID
                        {sortable && <i className={`fa ${getSortIcon('caseId')}`}></i>}
                      </th>
                      <th
                        style={{ width: '85px' }}
                        className={sortable ? 'sortable' : ''}
                        onClick={() => toggleSort('callDate')}
                      >
                        Date/Time
                        {sortable && <i className={`fa ${getSortIcon('callDate')}`}></i>}
                      </th>
                      <th style={{ width: '175px' }}>Nature of Call</th>
                      <th style={{ width: '175px' }}>Incident Location</th>
                      {showRoles && <th style={{ width: '100px' }}>Roles</th>}
                      {showOffenses && <th style={{ width: '100px' }}>Offenses</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {displayReports.map((report, index) => (
                      <tr key={'report-' + index}>
                        <td>
                          <a href={getReportLink(report)} className="link-primary">
                            {report.caseId || 'N/A'}
                          </a>
                        </td>
                        <td>{formatDateTime(report.callDate)}</td>
                        <td dangerouslySetInnerHTML={{ __html: formatNatureOfCall(report) }}></td>
                        <td>{report.callLocFormattedAddress || ''}</td>
                        {showRoles && <td>{getRolesDisplay(report.roles)}</td>}
                        {showOffenses && <td>{getOffensesDisplay(report.offenses)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">No reports involving this {entityType}</div>
              )}
            </div>
          ) : (
            /* Mobile Cards */
            <div className="mobile-cards">
              {displayReports.map((report, index) => (
                <div key={'report-mobile-' + index} className="card">
                  <div className="card-header">
                    <a href={getReportLink(report)} className="card-title">
                      {report.caseId || 'N/A'}
                    </a>
                    <span className="datetime">{formatDateTime(report.callDate)}</span>
                  </div>
                  <div className="card-body">
                    <div className="card-field">
                      <span className="field-label">Nature of Call:</span>
                      <span className="field-value" dangerouslySetInnerHTML={{ __html: formatNatureOfCall(report) }}></span>
                    </div>
                    {report.callLocFormattedAddress && (
                      <div className="card-field">
                        <span className="field-label">Location:</span>
                        <span className="field-value">{report.callLocFormattedAddress}</span>
                      </div>
                    )}
                    {showRoles && report.roles && report.roles.length > 0 && (
                      <div className="card-field">
                        <span className="field-label">Roles:</span>
                        <span className="field-value">{getRolesDisplay(report.roles)}</span>
                      </div>
                    )}
                    {showOffenses && report.offenses && report.offenses.length > 0 && (
                      <div className="card-field">
                        <span className="field-label">Offenses:</span>
                        <span className="field-value">{getOffensesDisplay(report.offenses)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {displayReports.length === 0 && (
                <div className="empty-state">No reports involving this {entityType}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDReportsGridReact = NPSDReportsGrid;
